import { ReactNode, createContext, useContext, useMemo } from 'react';

import { ChallengeTimelineItemType } from 'features/challenge/ChallengeTimelineItem';
import {
  INonRepeatedChallenge,
  IRepeatedChallenge,
} from 'features/challenge/config/types';
import {
  filterChallengesByGoal,
  usePlanningBacklogChooser,
  useSelectedGoalsContext,
} from 'features/planning';

type PlanningBacklogFilterContextType = {
  filteredChallenges: (
    | INonRepeatedChallenge
    | IRepeatedChallenge
    | ChallengeTimelineItemType
  )[];
};

const defaultPlanningBacklogFilterContext: PlanningBacklogFilterContextType = {
  filteredChallenges: [],
};

const PlanningBacklogFilterContext = createContext(
  defaultPlanningBacklogFilterContext
);

export const PlanningBacklogFilterProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { selectedGoalsIds } = useSelectedGoalsContext();
  const { data: backlogData } = usePlanningBacklogChooser();

  const filteredChallenges = useMemo(() => {
    if (!backlogData) {
      return [];
    }

    return !selectedGoalsIds.length
      ? backlogData.items
      : filterChallengesByGoal({
          challenges: backlogData.items,
          selectedGoalsIds,
        });
  }, [selectedGoalsIds, backlogData]);

  const contextValue = useMemo(
    () => ({
      filteredChallenges,
    }),
    [filteredChallenges]
  );

  return (
    <PlanningBacklogFilterContext.Provider value={contextValue}>
      {children}
    </PlanningBacklogFilterContext.Provider>
  );
};

export const usePlanningBacklogFilterContext = () => {
  const planningBacklogFilterContext = useContext(PlanningBacklogFilterContext);

  if (!planningBacklogFilterContext) {
    throw new Error(
      'usePlanningBacklogFilterContext must be used within a PlanningBacklogFilterContext.Provider'
    );
  }

  return planningBacklogFilterContext;
};
